import { Link, NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/LangContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import CurrencySwitcher from "@/components/CurrencySwitcher";
import { Menu, X, ShieldCheck, Package } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";

const FINAL_STATUSES = new Set(["delivered", "exception"]);

export default function Navbar() {
  const { user, logout } = useAuth();
  const t = useT();
  const [open, setOpen] = useState(false);
  const [inTransit, setInTransit] = useState(0);

  useEffect(() => {
    if (!user || user === false || user.role === "admin") { setInTransit(0); return; }
    let stop = false;
    const load = () => {
      api.get("/shipments/mine")
        .then((r) => {
          if (stop) return;
          const active = (r.data || []).filter((s) => !FINAL_STATUSES.has(s.current_status)).length;
          setInTransit(active);
        })
        .catch(() => {});
    };
    load();
    const i = setInterval(load, 60_000); // refresh every minute
    return () => { stop = true; clearInterval(i); };
  }, [user]);

  const NavItem = ({ to, children, testid }) => (
    <NavLink
      to={to}
      onClick={() => setOpen(false)}
      data-testid={testid}
      className={({ isActive }) =>
        `text-sm font-semibold transition-colors hover:text-[var(--accent)] ${
          isActive ? "text-[var(--accent)]" : "text-[var(--ink)]"
        }`
      }
    >
      {children}
    </NavLink>
  );

  return (
    <header className="glass-nav sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
          <span className="brand-mark text-2xl">
            nego<span className="text-[var(--accent)]">sino</span>.
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <NavItem to="/" testid="nav-home">{t("nav.home")}</NavItem>
          <NavItem to="/services" testid="nav-services">{t("nav.services")}</NavItem>
          <NavItem to="/secteurs" testid="nav-sectors">{t("nav.sectors")}</NavItem>
          <NavItem to="/produits" testid="nav-products">{t("nav.products")}</NavItem>
          <NavItem to="/fret" testid="nav-freight">{t("nav.freight")}</NavItem>
          <NavItem to="/transfert" testid="nav-transfer">{t("nav.transfer")}</NavItem>
          <NavItem to="/etudes" testid="nav-study">{t("nav.study")}</NavItem>
          <NavItem to="/a-propos" testid="nav-about">{t("nav.about")}</NavItem>
          <NavItem to="/contact" testid="nav-contact">{t("nav.contact")}</NavItem>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher />
          <CurrencySwitcher />
          {user && user.role === "admin" && (
            <Link to="/admin" className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--secondary)] flex items-center gap-1" data-testid="nav-admin">
              <ShieldCheck size={14} /> {t("nav.admin")}
            </Link>
          )}
          {user && user !== false ? (
            <>
              <Link to="/dashboard" className="text-sm font-semibold inline-flex items-center gap-1.5 relative" data-testid="nav-dashboard">
                {user.name?.split(" ")[0]}
                {inTransit > 0 && (
                  <span
                    className="inline-flex items-center gap-1 bg-[var(--accent)] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                    title={`${inTransit} colis en cours de livraison`}
                    data-testid="nav-shipment-badge"
                  >
                    <Package size={10} /> {inTransit}
                  </span>
                )}
              </Link>
              <button onClick={logout} className="btn-outline !py-2 !px-4 text-xs" data-testid="nav-logout">
                {t("nav.logout")}
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm font-semibold" data-testid="nav-login">{t("nav.login")}</Link>
              <Link to="/register" className="btn-primary !py-2 !px-4 text-sm" data-testid="nav-register">
                {t("nav.register")}
              </Link>
            </>
          )}
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <LanguageSwitcher />
          <CurrencySwitcher />
          <button className="p-2" onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-black/5 bg-white">
          <div className="px-6 py-4 flex flex-col gap-4">
            <NavItem to="/" testid="m-nav-home">{t("nav.home")}</NavItem>
            <NavItem to="/services" testid="m-nav-services">{t("nav.services")}</NavItem>
            <NavItem to="/secteurs" testid="m-nav-sectors">{t("nav.sectors")}</NavItem>
            <NavItem to="/produits" testid="m-nav-products">{t("nav.products")}</NavItem>
            <NavItem to="/fret" testid="m-nav-freight">{t("nav.freight")}</NavItem>
            <NavItem to="/transfert" testid="m-nav-transfer">{t("nav.transfer")}</NavItem>
            <NavItem to="/etudes" testid="m-nav-study">{t("nav.study")}</NavItem>
            <NavItem to="/a-propos" testid="m-nav-about">{t("nav.about")}</NavItem>
            <NavItem to="/contact" testid="m-nav-contact">{t("nav.contact")}</NavItem>
            {user && user.role === "admin" && (
              <Link to="/admin" onClick={() => setOpen(false)} className="font-semibold" data-testid="m-nav-admin">{t("nav.admin")}</Link>
            )}
            {user && user !== false ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="font-semibold" data-testid="m-nav-dashboard">{t("nav.dashboard")}</Link>
                <button onClick={() => { setOpen(false); logout(); }} className="btn-outline" data-testid="m-nav-logout">{t("nav.logout")}</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="font-semibold" data-testid="m-nav-login">{t("nav.login")}</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="btn-primary" data-testid="m-nav-register">{t("nav.register")}</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
